import { getConsultationTypes } from '../api/booking-service';
import { getSpecialists } from '../api/specialists-service';
import { EMPTY_PUBLIC_SITE_DATA, type PublicSiteData } from '../models/public-site-data';
import { BUILD_SITE_DATA_ELEMENT_ID, LIVE_SITE_DATA_ELEMENT_ID, parseSiteData } from './embedded-site-data';
import type { SiteDataSnapshot } from './site-data-snapshot';

/**
 * The specialists and the services, for every page that shows either.
 *
 * **Three sources, best first, and the page never waits on the last one.**
 *
 * 1. The live file, which nginx inlines into the page it serves and the API rewrites whenever a
 *    card or a service changes — the same arrangement as an article's page. When it is there the
 *    page needs no request at all.
 * 2. The build's copy, which the prerendered markup was drawn from. Hydration must start from it,
 *    so it is the server snapshot, and it is what is shown when the live file is missing.
 * 3. The anonymous routes, asked once in the background only when there is no live file, so a
 *    change since the build still reaches a visitor on a host that has not mounted it.
 */
let prerenderData: PublicSiteData = EMPTY_PUBLIC_SITE_DATA;
let serverSnapshot: SiteDataSnapshot | null = null;
let clientSnapshot: SiteDataSnapshot | null = null;
let revalidating = false;

const listeners = new Set<() => void>();

/** Called by the build before it renders, so the markup it writes carries the lists. */
export function setPrerenderSiteData(data: PublicSiteData): void {
    prerenderData = data;
    serverSnapshot = null;
}

export function subscribeSiteData(listener: () => void): () => void {
    listeners.add(listener);

    return () => listeners.delete(listener);
}

/** What the build rendered with — in Node the data it was handed, in a browser its embedded copy. */
export function readServerSiteData(): SiteDataSnapshot {
    if (serverSnapshot === null) {
        const data =
            typeof document === 'undefined'
                ? prerenderData
                : (parseSiteData(document.getElementById(BUILD_SITE_DATA_ELEMENT_ID)?.textContent) ??
                  EMPTY_PUBLIC_SITE_DATA);

        serverSnapshot = { data, settled: false };
    }

    return serverSnapshot;
}

export function readClientSiteData(): SiteDataSnapshot {
    if (clientSnapshot === null) {
        const live = parseSiteData(document.getElementById(LIVE_SITE_DATA_ELEMENT_ID)?.textContent);

        // The same object as the server snapshot when there is no live copy, so hydration has
        // nothing to re-render.
        clientSnapshot = live === null ? readServerSiteData() : { data: live, settled: true };
    }

    return clientSnapshot;
}

/**
 * Asks the API once per page load, and only when the page carried no live copy.
 *
 * A route that fails keeps the list already on screen: the build's copy is a better answer than an
 * empty section. Either way the data counts as settled afterwards — there is nothing left to ask.
 */
export function revalidateSiteData(): void {
    if (typeof document === 'undefined' || revalidating || readClientSiteData().settled) {
        return;
    }

    revalidating = true;

    void Promise.allSettled([getSpecialists(), getConsultationTypes()]).then(([specialists, types]) => {
        const current = readClientSiteData().data;

        clientSnapshot = {
            data: {
                specialists: specialists.status === 'fulfilled' ? specialists.value : current.specialists,
                consultationTypes: types.status === 'fulfilled' ? types.value : current.consultationTypes
            },
            settled: true
        };

        revalidating = false;
        listeners.forEach((listener) => listener());
    });
}
